import { format, parse } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import { useForm } from "react-hook-form";
import { NotificationManager } from "react-notifications";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import Select from "react-select";
import { Actions as SCHEMEACTIONS } from "../../../store/actions/schemeActions";
import { DateInputFormat } from "../../common/DateComponents";
import Spinner from "../../common/Spinner";
import { CustomOption } from "../../common/custom-selectors/CustomOption";

const AddSchemeModal = ({
	saveScheme,
	modalScheme,
	modalSaving,
	paySchedules,
	schemeTypes,
	providers,
	reliefTypes,
	permitted,
}) => {
	const { handleSubmit } = useForm();

	const [pensionProvider, setPensionProvider] = useState({ value: "Other", label: "Other" });
	const [startDate, setStartDate] = useState(new Date());
	const [endDate, setEndDate] = useState(null);
	const [name, setName] = useState();
	const [schemeType, setSchemeType] = useState();
	const [reliefType, setReliefType] = useState();
	const [paySchedule, setPaySchedule] = useState();
	const [showAdvanced, setShowAdvanced] = useState(false);
	const [autoEnrolmentExcluded, setAutoEnrolmentExcluded] = useState();

	const disabled = useMemo(() => {
		return !permitted || pensionProvider?.isDisabled;
	}, [pensionProvider?.value, permitted]);

	const onSubmit = (data) => {
		data.id = modalScheme?.id;
		data.provider = pensionProvider?.value;
		data.schemeType = schemeType;
		data.paySchedule = paySchedule?.value;
		data.name = name;
		data.startDate = startDate ? format(startDate, "yyyy-MM-dd") : null;
		data.endDate = endDate ? format(endDate, "yyyy-MM-dd") : "null";
		data.reliefType = reliefType;
		data.autoEnrolmentExcluded = autoEnrolmentExcluded;

		if (
			data.schemeType &&
			data.paySchedule &&
			data.name?.trim() &&
			data.startDate &&
			data.provider &&
			data.reliefType
		) {
			saveScheme(data);
		} else {
			NotificationManager.error("Please Ensure All Fields Are Filled!", "Error Saving Group");
		}
	};

	useEffect(() => {
		if (modalScheme?.id) {
			setStartDate(parse(modalScheme.startDate, "yyyy-MM-dd", new Date()));
			if (modalScheme.endDate && modalScheme.endDate !== "null")
				setEndDate(parse(modalScheme.endDate, "yyyy-MM-dd", new Date()));
			setName(modalScheme.name);
			setPensionProvider(providers?.find((item) => item.value == modalScheme.provider));
			setPaySchedule(paySchedules?.find((item) => item.value == modalScheme.paySchedule));

			if (schemeTypes?.find((item) => item.id == modalScheme.schemeType)?.advanced) {
				setShowAdvanced(true);
			}
			setSchemeType(modalScheme.schemeType);
			setReliefType(modalScheme.reliefType);

			setAutoEnrolmentExcluded(modalScheme?.autoEnrolmentExcluded);

		} else {
			setStartDate();
			setEndDate();
			setName();
			setShowAdvanced(false);
			setPensionProvider({ value: "Other", label: "Other" });
			setPaySchedule(paySchedules?.[0]);
			setSchemeType(null);
			setReliefType(null);
		}
	}, [modalScheme?.id]);

	const RadioOption = ({ label, item, value, onClick, columnSize }) => {
		if (!columnSize) columnSize = 4;
		return (
			<div className={`col-xs-${columnSize} pl-0`}>
				<div
					style={{ display: "flex", background: "white" }}
					className="radio-container"
				>
					<label
						htmlFor={`${label}${item.id}`}
						className="radio-block"
						style={{ padding: 0, margin: 10, width: "80%" }}
					>
						{item.name}
						<p>{item.description}</p>
					</label>
					<input
						style={{ width: "2.5rem", marginRight: 5 }}
						id={`${label}${item.id}`}
						type="radio"
						disabled={disabled}
						name={`${label}Options`}
						checked={value == item.id}
						onClick={() => {
							onClick(item.id);
						}}
						value={item.id}
					/>
				</div>
			</div>
		);
	};

	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			style={{ overflow: "visible" }}
		>
			<div
				className="form-section col-xs-12"
				style={{ paddingBottom: "2rem" }}
			>
				<div className="col-xs-12 section-header">
					<h1 className="block-title">Scheme Details</h1>
				</div>
				<div className="col-xs-12">
					<div className="col-xs-4 pl-0">
						<div className="form-material">
							<label htmlFor="material-text">Pension Provider</label>
							<Select
								isDisabled={disabled}
								options={providers}
								value={pensionProvider}
								components={{ Option: CustomOption }}
								onChange={(selected) => {
									setPensionProvider(selected);
								}}
							/>
						</div>
						<div className="clearfix" />
					</div>
					<div className="col-xs-4 pl-0">
						<div className="form-material">
							<label htmlFor="material-text">Scheme Start Date</label>
							<DatePicker
								showMonthDropdown
								showYearDropdown
								autoComplete="off"
								dateFormat="dd/MM/yyyy"
								className="form-control"
								required
								disabled={disabled}
								wrapperClassName="full-width"
								selected={startDate}
								onSelect={(date) => {
									setStartDate(date);
								}}
								customInput={<DateInputFormat />}
							/>
						</div>
						<div className="clearfix" />
					</div>
					<div className="col-xs-4 pl-0">
						<div className="form-material">
							<label htmlFor="material-text">Scheme End Date</label>
							<DatePicker
								showMonthDropdown
								showYearDropdown
								autoComplete="off"
								dateFormat="dd/MM/yyyy"
								className="form-control"
								wrapperClassName="full-width"
								selected={endDate}
								disabled={disabled}
								onChange={(date) => {
									setEndDate(date);
								}}
								customInput={<DateInputFormat />}
							/>
						</div>
						<div className="clearfix" />
					</div>
				</div>
				<div className="col-xs-12">
					<div className="col-xs-4 pl-0">
						<div className="form-material">
							<label htmlFor="material-text">Pay Schedule</label>
							<Select
								options={paySchedules}
								isDisabled={paySchedules?.length <= 1 || disabled}
								value={paySchedule}
								onChange={(selected) => {
									setPaySchedule(selected);
								}}
							/>
						</div>
						<div className="clearfix" />
					</div>
					<div className="col-xs-4 pl-0">
						<div className="form-material">
							<label htmlFor="material-text">Scheme Name</label>
							<input
								className="form-control form-input"
								autoComplete="off"
								disabled={disabled}
								value={name}
								required
								onChange={(e) => {
									setName(e.target.value);
								}}
							/>
						</div>
						<div className="clearfix" />
					</div>
					<div className="col-xs-4 pt-5">
						<div className="form-material">
							<label className="css-input switch switch-sm switch-success">
								<input
									name="autoEnrolmentExcluded"
									checked={autoEnrolmentExcluded}
									type="checkbox"
									disabled={disabled}
									onChange={(e) => {
										setAutoEnrolmentExcluded(e.target.checked)
									}}
								/>
								<span />
								<label style={{ marginBottom: 0 }}>Exclude from Auto Enrolment</label>
							</label>
						</div>
						<div className="clearfix" />
					</div>
				</div>
			</div>

			<div
				className="form-section col-xs-12"
				style={{ paddingBottom: "2rem" }}
			>
				<div className="col-xs-12 pl-0">
					<div className="col-xs-6 section-header">
						<h1 className="block-title">Scheme Type</h1>
					</div>
					<div className="col-xs-6 pl-0">
						<button
							className="btn btn-default pull-right"
							type="button"
							disabled={disabled}
							style={{ marginTop: 10 }}
							onClick={(e) => {
								e.preventDefault();
								setShowAdvanced(!showAdvanced);
							}}
						>
							{showAdvanced ? "Hide" : "Show"} Advanced
						</button>
					</div>
				</div>

				<div className="row equal-height">
					{schemeTypes
						?.filter((scheme) => !scheme.advanced)
						?.map((scheme) => (
							<RadioOption
								{...{ value: schemeType, onClick: setSchemeType, label: "scheme", item: scheme }}
							/>
						))}
				</div>

				{showAdvanced && (
					<div
						className="row equal-height"
						style={{ marginTop: 20 }}
					>
						{schemeTypes
							?.filter((scheme) => scheme.advanced)
							?.map((scheme) => (
								<RadioOption
									{...{ value: schemeType, onClick: setSchemeType, label: "scheme", item: scheme }}
								/>
							))}
					</div>
				)}
			</div>

			<div
				className="form-section col-xs-12"
				style={{ paddingBottom: "2rem" }}
			>
				<div className="col-xs-12 section-header">
					<h1 className="block-title">Relief Type</h1>
				</div>
				<div className="row equal-height">
					{reliefTypes?.map((relief) => (
						<RadioOption
							{...{
								value: reliefType,
								onClick: setReliefType,
								label: "relief",
								item: relief,
								columnSize: 6,
							}}
						/>
					))}
				</div>
			</div>

			<div className="col-xs-12 pr-0">
				<button
					className="btn btn-success pull-right"
					type="submit"
					disabled={disabled}
				>
					{!modalSaving ? (
						modalScheme ? (
							"Save"
						) : (
							"Create"
						)
					) : (
						<>
							<Spinner
								style={{ height: "1.5em", verticalAlign: "middle", float: "left" }}
								className="animate-spin -ml-1 mr-3 h-5 w-5 "
							/>
							<div style={{ float: "left" }}> Saving </div>
						</>
					)}
				</button>
			</div>
			<div className="clearfix" />
		</form>
	);
};

const mapStateToProps = (state) => {
	const pensionIntegrations = Object.values({ ...state?.integration?.pension }).map(({ name, canSyncUp }) => ({
		value: name,
		label: name,
		isDisabled: !canSyncUp,
		statusMessage: !canSyncUp ? (
			<div>
				{name} does not support creation of schemes outside of their product. To import a scheme, please use
				the&nbsp;
				<Link to="/organisation/settings/integrations">import feature</Link>
			</div>
		) : undefined,
	}));

	const providerOptions = [{ value: "Other", label: "Other" }, ...pensionIntegrations];

	const schemeTypeOptions = [
		{
			id: "banded",
			name: "Banded",
			description: "Pensionable Earnings are taken on Qualifying Earnings as the minimum earnings",
		},
		{
			id: "unbanded",
			name: "Unbanded",
			description: "Pensionable Earnings are taken on total Gross Qualifying Earnings",
		},
		{
			id: "type1",
			name: "Type 1",
			description:
				"Pensionable Earnings are at least or equal to the Basic Pay (Salary, Holiday Pay, Statutory Pay)",
			advanced: true,
		},
		{
			id: "type2",
			name: "Type 2",
			description: "Pensionable Earnings are at least 85% of Total Pay",
			advanced: true,
		},
		{ id: "type3", name: "Type 3", description: "Pensionable Earnings are worked on Total Pay", advanced: true },
	];
	const reliefTypes = [
		{
			id: "source",
			name: "Relief At Source",
			description:
				"The deduction is made *after tax calculation but less 20% - and 20% will then be added by the Pension Fund claimed back from HMRC",
		},
		{
			id: "net",
			name: "Net Pay Arrangements",
			description:
				"The pension contribution is deducted in full from the Gross Amount, and the employee does not get any additional tax refund",
		},
	];

	return {
		modalScheme: state?.pensions?.scheme?.modalScheme,
		modalSaving: state?.pensions?.scheme?.modalSaving,
		paySchedules: state.company?.settings?.paySchedules?.map((ps) => ({ value: ps.id, label: ps.name })),
		schemeTypes: schemeTypeOptions,
		providers: providerOptions,
		reliefTypes,
		permitted: true,
	};
};

const mapDispatchToProps = (dispatch) => ({
	saveScheme: (scheme) => dispatch(SCHEMEACTIONS.saveScheme(scheme)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddSchemeModal);
